import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DateFormats } from 'src/constants/dateFormats';
import { routes } from 'src/routes';
import { ListTicket, TicketStatus } from 'src/store/models/Ticket';
import { formatDate } from 'src/utils/dateFormatter';

import Button from '../Button/Button';
import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './TicketItem.module.scss';

export interface TicketItemProps {
  ticket?: ListTicket;
  className?: string;
  loading?: boolean;
}

const TicketItem: FC<TicketItemProps> = ({
  ticket,
  className,
  loading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isUnassigned = ticket?.status === TicketStatus.UNASSIGNED;

  const buttonLabel = (): string => {
    switch (ticket?.status) {
      case TicketStatus.TO_DO:
        return t('common:start');
      case TicketStatus.IN_PROGRESS:
        return t('common:in_progress');
      case TicketStatus.RESOLVED:
        return t('common:resolved');
      default:
        return t('common:assign');
    }
  };

  const dateLabel = isUnassigned ? t('common:reported') : t('common:assigned');

  const date = isUnassigned ? ticket?.createdAt : ticket?.assignedAt;

  return (
    <ShadowBox
      className={classNames(
        {
          [classes.ToDo]: ticket?.status === TicketStatus.TO_DO,
          [classes.InProgress]: ticket?.status === TicketStatus.IN_PROGRESS,
          [classes.Resolved]: ticket?.status === TicketStatus.RESOLVED,
          [classes.Unassigned]: isUnassigned,
        },
        classes.Container,
        className
      )}
      onClick={() => {
        if (isUnassigned) {
          navigate(`${routes.TICKETS}/${ticket?.id}${routes.ASSIGN}`);
        } else {
          navigate(`${routes.TICKETS}/${ticket?.id}`);
        }
      }}
    >
      {!loading ? (
        <>
          <p>{isUnassigned ? t('common:unassigned') : ticket?.ticketId}</p>
          <p>{ticket?.owner}</p>
          <p>{ticket?.address}</p>
          <p>{ticket?.model}</p>
          <footer>
            <p>
              {`${dateLabel}: ${formatDate(
                date,
                DateFormats.TICKET_LIST_FORMAT
              )}`}
            </p>
            <Button size="small">{buttonLabel().toUpperCase()}</Button>
          </footer>
        </>
      ) : (
        <div className={classes.Loading}>
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="text" />
          <Skeleton animation="wave" variant="text" />
        </div>
      )}
    </ShadowBox>
  );
};

export default TicketItem;
